import { useState, useCallback, useRef } from 'react';
import { createLogger } from '@/utils/logger/devLogger';
import { isValidEmailAddress } from '@/utils/validation/emailValidation';

const emailValidationLogger = createLogger('EmailValidation');

interface EmailValidationResult {
  available: boolean;
  error?: string;
  /**
   * #2300: machine-readable conflict code from /api/validation/check-email
   * (see EMAIL_UNAVAILABLE_REASONS). Callers that treat some conflicts as
   * advisory rather than blocking MUST branch on this, never on `error` copy.
   */
  reason?: string;
}

interface UseEmailValidationOptions {
  debounceMs?: number;
  onValidationResult?: (result: EmailValidationResult | null) => void;
}

export function useEmailValidation(options: UseEmailValidationOptions = {}) {
  const { debounceMs = 800, onValidationResult } = options;
  const [isChecking, setIsChecking] = useState(false);
  const [lastResult, setLastResult] = useState<EmailValidationResult | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const checkEmailAvailability = useCallback(async (email: string, currentEntityId?: string, entityType?: string): Promise<EmailValidationResult | null> => {
    emailValidationLogger.debug('🔍 Starting email validation check:', { email, currentEntityId, entityType });
    
    try {
      // Get auth token from Supabase (optional for public registration flows)
      const supabase = await import('@/utils/supabase/client').then(mod => mod.supabase);
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      emailValidationLogger.debug('Session check:', { 
        hasSession: !!session, 
        hasAccessToken: !!session?.access_token,
        sessionError,
        userId: session?.user?.id
      });
      
      if (sessionError) {
        emailValidationLogger.error('Session error:', sessionError);
      }
      
      // Build headers - include auth token if available, but don't require it
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      };
      
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
        emailValidationLogger.debug('Including auth token in request');
      } else {
        emailValidationLogger.info('No auth session - proceeding with public validation');
      }

      emailValidationLogger.debug('Making API request to:', '/api/validation/check-email', {
        email,
        currentEntityId,
        entityType,
        isAuthenticated: !!session?.access_token
      });

      const response = await fetch('/api/validation/check-email', {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          email,
          currentEntityId,
          entityType
        })
      });

      emailValidationLogger.debug('Response received:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });

      let responseData;
      try {
        const responseText = await response.text();
        emailValidationLogger.debug('Raw response text:', { 
          text: responseText,
          length: responseText.length,
          isEmpty: !responseText.trim()
        });
        
        if (!responseText.trim()) {
          emailValidationLogger.error('Empty response received from API', {
            status: response.status,
            statusText: response.statusText,
            headers: Object.fromEntries(response.headers.entries())
          });
          return null;
        }
        
        responseData = JSON.parse(responseText);
        emailValidationLogger.debug('Parsed API response:', { 
          status: response.status, 
          statusText: response.statusText,
          data: responseData,
          hasError: !!responseData.error,
          hasAvailable: responseData.available !== undefined
        });
      } catch (jsonError) {
        emailValidationLogger.error('Failed to parse JSON response:', {
          jsonError,
          responseStatus: response.status,
          responseStatusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries())
        });
        return null;
      }

      if (!response.ok) {
        // 409 Conflict is a valid response (email already in use), not an error
        if (response.status === 409) {
          emailValidationLogger.info('Email validation found conflict (email in use):', {
            status: response.status,
            email: email.substring(0, 3) + '***', // Partial email for privacy
            conflictMessage: responseData?.error || 'Email is already in use'
          });
          
          return {
            available: false,
            error: responseData?.error || 'Email is already in use',
            reason: responseData?.reason
          };
        }
        
        // Log actual errors (non-409 responses)
        emailValidationLogger.error('Email validation API error:', {
          status: response.status,
          statusText: response.statusText,
          responseData,
          responseDataType: typeof responseData,
          responseDataKeys: responseData ? Object.keys(responseData) : 'No keys',
          isEmpty: !responseData || Object.keys(responseData).length === 0
        });
        
        // For other errors, return null to indicate validation failed
        return null;
      }

      // Success response - email is available
      const result: EmailValidationResult = {
        available: responseData.available,
        error: responseData.error,
        reason: responseData.reason
      };
      
      emailValidationLogger.debug('Email validation result:', result);
      return result;

    } catch (error) {
      emailValidationLogger.error('🚨 Email validation network error:', error);
      emailValidationLogger.error('🚨 Error type:', typeof error);
      emailValidationLogger.error('🚨 Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : 'No stack trace'
      });
      return null;
    }
  }, []);

  const validateEmail = useCallback((email: string, currentEntityId?: string, entityType?: string) => {
    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Reset state for new validation
    setLastResult(null);

    // Basic client-side validations first
    if (!email || !email.trim()) {
      return; // Don't validate empty emails
    }

    if (!isValidEmailAddress(email)) {
      return; // Don't validate invalid format emails
    }

    // Debounced server-side validation
    setIsChecking(true);
    timeoutRef.current = setTimeout(async () => {
      const result = await checkEmailAvailability(email.trim(), currentEntityId, entityType);
      setIsChecking(false);
      setLastResult(result);
      
      if (onValidationResult) {
        onValidationResult(result);
      }
    }, debounceMs);
  }, [debounceMs, checkEmailAvailability, onValidationResult]);

  const clearValidation = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsChecking(false);
    setLastResult(null);
  }, []);

  return {
    validateEmail,
    clearValidation,
    isChecking,
    lastResult
  };
} 